import { Typography } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import type { CSSObject, Theme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import Image from 'next/image';
import Link from 'next/link';
import type { MouseEvent } from 'react';
import { Fragment, useState } from 'react';

import MainMenuItem from '@/app/components/CommonLayout/MainMenuItem';
import { SIDEBAR } from '@/app/components/Provider/ThemeProvider/constant';
import { hideScroll } from '@/app/components/Provider/ThemeProvider/css';
import { usePermissions } from '@/hooks/usePermissions';
import { useSelector } from '@/stores';

const openedMixin = (theme: Theme): CSSObject => ({
  width: SIDEBAR.WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  ...hideScroll.y,
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  ...hideScroll.y,
  width: theme.spacing(0),
  [theme.breakpoints.up('sm')]: {
    width: theme.spacing(7),
  },
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  width: SIDEBAR.WIDTH,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

const DrawerHeader = styled(Link)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(0, 1),
  position: 'sticky',
  top: 0,
  background: '#FFF',
  zIndex: 1,
  cursor: 'pointer',
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const Sidebar = () => {
  const [open, setOpen] = useState<{ [p: string]: boolean }>({});
  const uiState = useSelector((state) => state.ui);
  const { MenuSidebar } = usePermissions();

  const handleCollapseClick = (evt: MouseEvent, menu: string) => {
    evt.stopPropagation();
    setOpen((open) => {
      const newOpen = { ...open };
      newOpen[menu] = newOpen[menu] === false;
      return newOpen;
    });
  };

  return (
    <Drawer variant="permanent" open={uiState.openDrawer}>
      <DrawerHeader href="/">
        <Image alt="giao-duc-nhat-anh" src="/logo.png" height={36} width={36} />
        <Typography variant="h4" ml={1}>
          ERP Website
        </Typography>
      </DrawerHeader>
      <List dense>
        {MenuSidebar.map((menu) => {
          if (!menu?.sub) {
            return <MainMenuItem key={menu.path} link={menu.path} iconName={menu.icon} label={menu.label} />;
          } else {
            return (
              <Fragment key={menu.path}>
                {uiState.openDrawer && (
                  <MainMenuItem
                    iconName={menu.icon}
                    label={menu.label}
                    onClick={(evt) => handleCollapseClick(evt, menu.path)}
                  />
                )}
                <Collapse in={open[menu.path] !== false || !uiState.openDrawer} timeout="auto" unmountOnExit>
                  <List dense component="div" disablePadding>
                    {menu.sub.map((sub) => {
                      return (
                        <MainMenuItem
                          key={sub.path}
                          link={sub.path}
                          iconName={sub.icon}
                          label={sub.label}
                          sx={uiState.openDrawer ? { paddingLeft: 4 } : null}
                        />
                      );
                    })}
                  </List>
                </Collapse>
              </Fragment>
            );
          }
        })}
      </List>
    </Drawer>
  );
};

export default Sidebar;
